/* =====================================================================
 * @auw/permissions — React bindings.
 *
 * `<PermissionProvider>` captures the tenant's role tree + the current
 * user's role id once at the top of the tree. Down-tree components use
 * `usePermission(p)` / `usePermissions([...])` to read granted flags,
 * or `<Permit permission={p}>` for inline render gating.
 *
 * Keeping React surface in a dedicated module lets non-React
 * consumers (build scripts, SSR data loaders, tests) import the pure
 * policy functions without pulling in React.
 * ===================================================================== */

import React, {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from 'react';

import { collectGrants, isPermissionGranted } from './policy';
import type {
  Permission,
  PermissionContext as PermissionContextData,
  RoleDefinition,
} from './types';

export interface PermissionContextValue {
  readonly roles: readonly RoleDefinition[];
  readonly userRoleId: string | null;
  readonly can: (permission: Permission) => boolean;
}

const EMPTY_ROLES: readonly RoleDefinition[] = Object.freeze([]);

const PermissionReactContext = createContext<PermissionContextValue>({
  roles: EMPTY_ROLES,
  userRoleId: null,
  can: () => false,
});

export interface PermissionProviderProps {
  readonly roles?: readonly RoleDefinition[];
  readonly userRoleId?: string | null;
  readonly children: ReactNode;
}

export const PermissionProvider: React.FC<PermissionProviderProps> = ({
  roles,
  userRoleId,
  children,
}) => {
  const value = useMemo<PermissionContextValue>(() => {
    const resolvedRoles = roles ?? EMPTY_ROLES;
    const resolvedUserRoleId = userRoleId ?? null;
    const ctx: PermissionContextData = {
      roles: resolvedRoles,
      userRoleId: resolvedUserRoleId,
    };
    return {
      roles: resolvedRoles,
      userRoleId: resolvedUserRoleId,
      can: (permission: Permission) => isPermissionGranted(ctx, permission),
    };
  }, [roles, userRoleId]);

  return (
    <PermissionReactContext.Provider value={value}>
      {children}
    </PermissionReactContext.Provider>
  );
};

/** Primary render-time check. */
export function usePermission(permission: Permission): boolean {
  return useContext(PermissionReactContext).can(permission);
}

/** Bulk check — returns a stable map keyed by permission id. */
export function usePermissions(
  permissions: readonly Permission[],
): Record<Permission, boolean> {
  const ctx = useContext(PermissionReactContext);
  return useMemo(
    () =>
      collectGrants(
        { roles: ctx.roles, userRoleId: ctx.userRoleId },
        permissions,
      ),
    [ctx.roles, ctx.userRoleId, permissions],
  );
}

/** Full context access — useful for showing the current role's label
 *  in profile menus / role-aware admin pages. */
export function usePermissionContext(): PermissionContextValue {
  return useContext(PermissionReactContext);
}

export interface PermitProps {
  readonly permission: Permission;
  readonly fallback?: ReactNode;
  readonly children: ReactNode;
}

/** Declarative render gate:
 *
 *  ```tsx
 *  <Permit permission={ADMIN_FEED_REFRESH}>
 *    <RegenerateButton />
 *  </Permit>
 *  ```
 */
export const Permit: React.FC<PermitProps> = ({
  permission,
  fallback = null,
  children,
}) => {
  const allowed = usePermission(permission);
  return <>{allowed ? children : fallback}</>;
};
